import { getFormattedDurationForStoryVideo } from '@src/utility/timeFormat';
import styled from 'styled-components';

import type { OtherVideo } from '@src/types.ts/story';
import type { FC } from 'react';

type Props = {
  /** In pick order — the same order and numbering as the selected cards. */
  clips: OtherVideo[];
};

/**
 * The clips a bulk delete is about to remove, listed by name inside the
 * confirmation dialog (PLAT-11118).
 *
 * A count alone asks the creator to confirm something they cannot check, so
 * every clip is listed — no truncation. The region is height-bounded and
 * scrolls instead, so a long selection can never push the dialog's actions off
 * screen.
 */
const ClipDeleteList: FC<Props> = ({ clips }) => (
  <List data-testid="clip-delete-list">
    {clips.map((clip, index) => {
      // The versions that come out with the clip (DS4) — the one part of
      // "everything being deleted" a title on its own hides.
      const versionCount = clip.associatedVideos?.length ?? 0;
      const { formattedDuration } = getFormattedDurationForStoryVideo(clip);
      return (
        <Row key={clip.id ?? index}>
          <Position>{index + 1}</Position>
          <Body>
            <Title title={clip.title || undefined}>
              {clip.title || 'Untitled clip'}
            </Title>
            {versionCount > 0 && (
              <Versions>
                +{versionCount} version{versionCount === 1 ? '' : 's'}
              </Versions>
            )}
          </Body>
          {formattedDuration !== '--:--' && (
            <Duration>{formattedDuration}</Duration>
          )}
        </Row>
      );
    })}
  </List>
);

export default ClipDeleteList;

const List = styled.ul`
  margin: 0;
  padding: var(--spacing-xs);
  list-style: none;
  /* ~6 rows, then scroll — the actions row stays reachable at any count. */
  max-height: 216px;
  overflow-y: auto;
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  background: var(--background);
`;

const Row = styled.li`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  padding: var(--spacing-xs) var(--spacing-sm);
`;

// Matches the number on the card behind the dialog (PLAT-11092), so a row and
// a card are recognisably the same clip.
const Position = styled.span`
  flex-shrink: 0;
  min-width: 16px;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--text-muted);
  line-height: 1.5;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  color: var(--foreground);
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Versions = styled.div`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

const Duration = styled.span`
  flex-shrink: 0;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--text-muted);
  line-height: 1.5;
`;
